import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { combineLatest, EMPTY } from 'rxjs';
import { catchError, finalize, switchMap, tap } from 'rxjs/operators';
import { DateTime } from 'luxon';

import { DashboardWidgetsModule } from 'app/inter-chain/dashboard/widgets/dashboard-widgets.module';
import { ConciergeSummaryService } from 'app/concierge/conversations/services/concierge-summary.service';
import { InteractionRateFilters } from 'app/concierge/models/Summary.model';
import { DashboardWidgetCardComponent } from '../dashboard-widget-card/dashboard-widget-card.component';
import { getAutomationRateLevel } from '../../utils/qualifications.util';

@Component({
  selector: 'mh-automation-rate-widget-container',
  templateUrl: './automation-rate-widget-container.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DashboardWidgetsModule, DashboardWidgetCardComponent, TranslateModule],
})
export class AutomationRateWidgetContainerComponent {
  customerId = input.required<number>();
  enabled = input(true);

  automationRate = signal<number | null>(null);
  displayValue = signal<string | null>(null);
  loading = signal(true);
  error = signal(false);
  ready = computed(() => !this.loading());

  qualificationKey = computed(() => `qualifys.nps.${getAutomationRateLevel(this.automationRate())}`);

  private destroyRef = inject(DestroyRef);
  private conciergeSummaryService = inject(ConciergeSummaryService);

  constructor() {
    combineLatest([toObservable(this.customerId), toObservable(this.enabled)])
      .pipe(
        switchMap(([, enabled]) => {
          this.reset();
          if (!enabled) {
            this.setDisabled();
            return EMPTY;
          }
          return this.fetchData();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private reset(): void {
    this.automationRate.set(null);
    this.displayValue.set(null);
    this.loading.set(true);
    this.error.set(false);
  }

  private setDisabled(): void {
    this.loading.set(false);
  }

  private fetchData() {
    const filters: InteractionRateFilters = {
      customer_ids: `${this.customerId()}`,
      start_date: DateTime.now().minus({ days: 30 }).toFormat('yyyy-MM-dd'),
      end_date: DateTime.now().toFormat('yyyy-MM-dd'),
    };

    return this.conciergeSummaryService.getAutomationRate(filters).pipe(
      tap((response) => {
        const automationData = Array.isArray(response?.data) ? response.data[0] : response?.data;
        const rate = automationData?.non_derivation_rate != null ? automationData.non_derivation_rate * 100 : null;
        this.automationRate.set(rate);
        this.displayValue.set(rate != null ? `${rate.toFixed()}%` : null);
      }),
      catchError(() => {
        this.error.set(true);
        return EMPTY;
      }),
      finalize(() => {
        this.loading.set(false);
      }),
    );
  }
}
